import React from 'react';
import { Link } from 'react-router-dom';
import { Mail } from 'lucide-react';

const MichaelHeneiseProfile = () => {
  const person = {
    id: "michael-heneise",
    name: "Michael T. Heneise",
    honorific: "Dr",
    title: "President",
    affiliation: "Associate Professor of Religious Studies and Social Anthropology, UiT The Arctic University of Norway; Co-Founder",
    image: "https://customer-assets.emergentagent.com/job_roadwise-asia/artifacts/8eyr5vpw_Heneise%20-%20passport%20blue.jpeg",
    email: "michael.t.heneise@uit.no",
    category: "Leadership Council"
  };

  const SectionDivider = () => (
    <div className="my-12 flex items-center justify-center">
      <div className="h-px w-24 bg-stone-300"></div>
    </div>
  );

  return (
    <div className="min-h-screen bg-white">
      {/* Breadcrumb Navigation */}
      <div className="bg-stone-50 border-b border-stone-200">
        <div className="container mx-auto px-4 py-4">
          <nav className="flex items-center text-sm text-stone-600">
            <Link to="/" className="hover:text-green-600 transition-colors">Home</Link>
            <span className="mx-2">/</span>
            <Link to="/people" className="hover:text-green-600 transition-colors">People</Link>
            <span className="mx-2">/</span>
            <span className="text-stone-900">{person.name}</span>
          </nav>
        </div>
      </div>

      {/* Identity Header */}
      <section className="bg-gradient-to-b from-stone-50 to-white py-16 border-b border-stone-200">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-col md:flex-row gap-8 items-start">
              {/* Profile Image */}
              <div className="flex-shrink-0">
                <div className="w-48 h-48 rounded-lg overflow-hidden bg-stone-200 shadow-lg">
                  <img 
                    src={person.image} 
                    alt={person.name}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>

              {/* Identity Information */}
              <div className="flex-1">
                <div className="mb-3">
                  <span className="inline-block px-3 py-1 bg-green-100 text-green-800 text-xs font-medium uppercase tracking-wider rounded-full">
                    {person.category}
                  </span>
                </div>
                <h1 className="text-4xl font-serif font-bold text-stone-900 mb-2">
                  <span className="text-stone-600">{person.honorific} </span>
                  {person.name}
                </h1>
                <p className="text-xl text-stone-700 font-medium mb-4">
                  {person.title}
                </p>
                <p className="text-stone-600 leading-relaxed">
                  {person.affiliation}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto">

          {/* Intellectual Biography */}
          <section className="mb-16">
            <h2 className="text-2xl font-serif font-bold text-stone-900 mb-6 pb-2 border-b-2 border-stone-200">
              Intellectual Biography
            </h2>
            <div className="prose prose-stone max-w-none">
              <p className="text-stone-700 leading-relaxed mb-4">
                Michael T. Heneise is a social and cultural anthropologist whose scholarship explores dreams, healing, cosmology, political agency, and the ontological entanglements of human and nonhuman life. His work is grounded in long-term ethnographic engagement in Highland Asia, especially among Naga and Karbi communities, and is informed by earlier experiences in Latin America and the Caribbean, where he first encountered Indigenous traditions of transformation, spirituality, and relational world-making.
              </p>
              <p className="text-stone-700 leading-relaxed mb-4">
                He is Associate Professor in the Department of Archaeology, History, and Religious Studies at UiT The Arctic University of Norway, where he teaches across religious studies, anthropology, theory and method, religion and politics, and environmental humanities. He holds a PhD in Social Anthropology from the University of Edinburgh. His doctoral research examined dreams, sovereignty, and prophetic authority among the Nagas, establishing a wider intellectual trajectory concerned with vision, alterity, landscape, and the unstable boundaries between persons, spirits, animals, and place.
              </p>
              <p className="text-stone-700 leading-relaxed mb-4">
                Heneise is Co-Founder and President of The Highland Institute in Northeast India, an independent research centre dedicated to long-term, interdisciplinary, and community-engaged scholarship across the eastern Himalayas and the India–Myanmar borderlands. Through the Institute, he has helped build research training initiatives, editorial platforms, and collaborative scholarly infrastructures connecting local knowledge traditions with international academic networks.
              </p>
              <p className="text-stone-700 leading-relaxed">
                His work brings anthropology into sustained dialogue with phenomenology, decolonial and postcolonial theory, medical anthropology, and the anthropology of symbols. Across research, teaching, and institution-building, he is especially interested in how dream narratives, healing practices, animal transformations, and ritual landscapes disclose overlapping ontologies and alternative ways of knowing.
              </p>
            </div>
          </section>

          <SectionDivider />

          {/* Research Foci / Areas of Work */}
          <section className="mb-16">
            <h2 className="text-2xl font-serif font-bold text-stone-900 mb-6 pb-2 border-b-2 border-stone-200">
              Research Foci & Areas of Work
            </h2>
            <div className="prose prose-stone max-w-none">
              <p className="text-stone-700 leading-relaxed mb-4">
                Michael T. Heneise's research is situated at the intersection of anthropology of religion, phenomenology, Indigenous knowledge studies, and environmental humanities. His work engages especially with dreams, visions, prophecy, healing, sovereignty, medical pluralism, cosmology, and human–nonhuman relations in Highland Asia.
              </p>
              <p className="text-stone-700 leading-relaxed mb-6">
                A recurring concern in his scholarship is the extent to which cosmological worlds are not merely symbolic systems, but lived, contested, and embodied realities. His research asks how communities articulate relations among ancestors, spirits, animals, landscapes, and political authority, and how such relations shape questions of illness, ritual, kinship, ecology, and historical change.
              </p>
              <p className="text-stone-700 font-medium mb-4">Key areas of work include:</p>
              <ul className="space-y-2">
                <li className="flex items-start gap-3 text-stone-700">
                  <span className="text-green-600 mt-1">•</span>
                  <span>Anthropology of dreams, visions, and prophecy</span>
                </li>
                <li className="flex items-start gap-3 text-stone-700">
                  <span className="text-green-600 mt-1">•</span>
                  <span>Indigenous cosmologies and knowledge systems</span>
                </li>
                <li className="flex items-start gap-3 text-stone-700">
                  <span className="text-green-600 mt-1">•</span>
                  <span>Medical anthropology and medical pluralism</span>
                </li>
                <li className="flex items-start gap-3 text-stone-700">
                  <span className="text-green-600 mt-1">•</span>
                  <span>Phenomenology and eco-phenomenology</span>
                </li>
                <li className="flex items-start gap-3 text-stone-700">
                  <span className="text-green-600 mt-1">•</span>
                  <span>Religion, politics, and sovereignty in Highland Asia</span>
                </li>
                <li className="flex items-start gap-3 text-stone-700">
                  <span className="text-green-600 mt-1">•</span>
                  <span>Human–nonhuman relations, transformation, and ontological plurality</span>
                </li>
                <li className="flex items-start gap-3 text-stone-700">
                  <span className="text-green-600 mt-1">•</span>
                  <span>Decolonial methods and community-engaged research</span>
                </li>
                <li className="flex items-start gap-3 text-stone-700">
                  <span className="text-green-600 mt-1">•</span>
                  <span>Environmental humanities in highland and Arctic contexts</span>
                </li>
              </ul>
            </div>
          </section>

          <SectionDivider />

          {/* Highland Institute Projects */}
          <section className="mb-16">
            <h2 className="text-2xl font-serif font-bold text-stone-900 mb-6 pb-2 border-b-2 border-stone-200">
              Highland Institute Projects
            </h2>
            <div className="prose prose-stone max-w-none">
              <p className="text-stone-700 leading-relaxed mb-4">
                At The Highland Institute, Michael T. Heneise has contributed to the development of long-term research infrastructures linking scholarship, publication, research training, and community engagement. His institutional work has focused on building interdisciplinary and transregional initiatives rooted in Highland Asia while remaining in dialogue with wider debates in anthropology, religious studies, and the environmental humanities.
              </p>
              <p className="text-stone-700 leading-relaxed mb-4">
                He is Principal Investigator of <strong>Ekologos – Global Environmental Humanities: Indigenous Knowledge about Environmental Change</strong>, an international collaboration anchored at UiT and developed with partners in India, Brazil, and Sápmi. The project examines Indigenous knowledge, sustainability, and environmental change across highland, Arctic, and marine worlds.
              </p>
              <p className="text-stone-700 leading-relaxed">
                He has also played a leading role in shaping the Institute's postgraduate research training initiatives, including the development of a postgraduate certificate programme designed to strengthen methodological, editorial, and analytical capacity among early-career scholars in Northeast India. His broader contributions include research mentorship, editorial development, institutional partnerships, and the cultivation of decolonial scholarly platforms responsive to local knowledge traditions and regional histories.
              </p>
            </div>
          </section>

          <SectionDivider />

          {/* Selected Publications & Knowledge Outputs */}
          <section className="mb-16">
            <h2 className="text-2xl font-serif font-bold text-stone-900 mb-6 pb-2 border-b-2 border-stone-200">
              Selected Publications & Knowledge Outputs
            </h2>
            <div className="prose prose-stone max-w-none">
              <p className="text-stone-700 font-medium mb-4">
                Selected books and edited volumes:
              </p>
              <ul className="space-y-3 mb-6">
                <li className="text-stone-700 leading-relaxed pl-4 border-l-2 border-green-600">
                  <em>Agency and Knowledge in Northeast India: The Life and Landscapes of Dreams</em> (Routledge, 2019)
                </li>
                <li className="text-stone-700 leading-relaxed pl-4 border-l-2 border-green-600">
                  <em>The Routledge Handbook of Highland Asia</em>, co-edited with Jelle J. P. Wouters (Routledge, 2023)
                </li>
                <li className="text-stone-700 leading-relaxed pl-4 border-l-2 border-green-600">
                  <em>The Routledge Handbook of Eco-Phenomenology</em>, co-edited with Cassandra Falke, Espen Dahl, Alice Sundman, and Edvard Lia (Routledge, 2026)
                </li>
                <li className="text-stone-700 leading-relaxed pl-4 border-l-2 border-green-600">
                  <em>Nagas in the 21st Century</em>, co-edited with Jelle J. P. Wouters (Highlander Books, 2017)
                </li>
                <li className="text-stone-700 leading-relaxed pl-4 border-l-2 border-green-600">
                  <em>Passing Things On: Ancestors and Genealogies in Northeast India</em>, edited volume (Heritage Publishing House, 2015)
                </li>
              </ul>
              <p className="text-stone-700 leading-relaxed">
                His articles and chapters address dreaming, healing, cosmopolitics, eco-phenomenology, religious change, and contemporary Naga thought across journals, companions, handbooks, and edited collections.
              </p>
            </div>
          </section>

          <SectionDivider />

          {/* Public Engagement, Teaching & Community Work */}
          <section className="mb-16">
            <h2 className="text-2xl font-serif font-bold text-stone-900 mb-6 pb-2 border-b-2 border-stone-200">
              Public Engagement, Teaching & Community Work
            </h2>
            <div className="prose prose-stone max-w-none">
              <p className="text-stone-700 leading-relaxed mb-4">
                Alongside his research, Michael T. Heneise is committed to teaching, mentorship, and public scholarship. At UiT, he teaches across bachelor's, master's, and teacher-education programmes in religious studies, including courses on religion and politics, South and East Asian religions, theory and method, environmental humanities, and anthropological approaches to religion and society.
              </p>
              <p className="text-stone-700 leading-relaxed">
                His work also extends beyond the university classroom. Through The Highland Institute, he has supported early-career researchers, contributed to editorial and publication initiatives, developed collaborative workshops and postgraduate programmes, and participated in wider public conversations on religion, sustainability, Indigenous knowledge, and the humanities. He is also a contributor to public-facing intellectual forums and interdisciplinary dialogues connecting anthropology, philosophy, religion, and contemporary social life.
              </p>
            </div>
          </section>

          <SectionDivider />

          {/* Contact & Scholarly Infrastructure */}
          <section className="mb-16">
            <h2 className="text-2xl font-serif font-bold text-stone-900 mb-6 pb-2 border-b-2 border-stone-200">
              Contact & Scholarly Infrastructure
            </h2>
            
            <div className="grid md:grid-cols-2 gap-8">
              {/* Institutional Affiliation */}
              <div className="bg-stone-50 p-6 rounded-lg">
                <h3 className="font-semibold text-stone-900 mb-3">Institutional Affiliation</h3>
                <p className="text-stone-700 leading-relaxed text-sm">
                  Department of Archaeology, History, and Religious Studies<br />
                  UiT The Arctic University of Norway<br />
                  Postboks 6050 Langnes<br />
                  9037 Tromsø, Norway
                </p>
              </div>

              {/* Email */}
              <div className="bg-stone-50 p-6 rounded-lg">
                <h3 className="font-semibold text-stone-900 mb-3">Email</h3>
                <div className="flex items-center gap-3 text-stone-700">
                  <Mail className="w-5 h-5 text-green-600" />
                  <a 
                    href="mailto:michael.t.heneise@uit.no"
                    className="hover:text-green-600 transition-colors"
                  >
                    michael.t.heneise@uit.no
                  </a>
                </div>
              </div>
            </div>

            {/* Professional Roles */}
            <div className="mt-8">
              <h3 className="font-semibold text-stone-900 mb-4">Professional Roles</h3>
              <ul className="space-y-2">
                <li className="flex items-start gap-3 text-stone-700">
                  <span className="text-green-600 mt-1">•</span>
                  <span>President and Co-Founder, The Highland Institute</span>
                </li>
                <li className="flex items-start gap-3 text-stone-700">
                  <span className="text-green-600 mt-1">•</span>
                  <span>Associate Professor, UiT The Arctic University of Norway</span>
                </li>
                <li className="flex items-start gap-3 text-stone-700">
                  <span className="text-green-600 mt-1">•</span>
                  <span>Editor-in-Chief, <em>HIMALAYA: Journal of the Association for Nepal and Himalayan Studies</em></span>
                </li>
                <li className="flex items-start gap-3 text-stone-700">
                  <span className="text-green-600 mt-1">•</span>
                  <span>Co-Founder, Highlander Journal</span>
                </li>
                <li className="flex items-start gap-3 text-stone-700">
                  <span className="text-green-600 mt-1">•</span>
                  <span>Co-Founder, Highlander Press</span>
                </li>
              </ul>
            </div>

            {/* Scholarly Profiles */}
            <div className="mt-8">
              <h3 className="font-semibold text-stone-900 mb-4">Scholarly Profiles</h3>
              <ul className="space-y-2">
                <li className="flex items-start gap-3 text-stone-700">
                  <span className="text-green-600 mt-1">•</span>
                  <a 
                    href="https://orcid.org/0000-0002-6115-5183"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-green-600 transition-colors"
                  >
                    ORCID
                  </a>
                </li>
                <li className="flex items-start gap-3 text-stone-700">
                  <span className="text-green-600 mt-1">•</span>
                  <a 
                    href="https://nva.sikt.no/research-profile/1113801"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-green-600 transition-colors"
                  >
                    NVA research profile
                  </a>
                </li>
                <li className="flex items-start gap-3 text-stone-700">
                  <span className="text-green-600 mt-1">•</span>
                  <a 
                    href="https://www.researchgate.net/profile/Michael-Heneise"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-green-600 transition-colors"
                  >
                    ResearchGate
                  </a>
                </li>
                <li className="flex items-start gap-3 text-stone-700">
                  <span className="text-green-600 mt-1">•</span>
                  <a 
                    href="https://jokstad.academia.edu/MichaelHeneise"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-green-600 transition-colors"
                  >
                    Academia.edu
                  </a>
                </li>
              </ul>
            </div>
          </section>

        </div>
      </div>

      {/* Footer Note */}
      <div className="bg-stone-50 border-t border-stone-200 py-8">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-sm text-stone-500 font-serif italic">
              This profile forms part of The Highland Institute's living scholarly archive, documenting research trajectories, collaborative commitments, and the intellectual work shaping the Institute's wider academic community.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MichaelHeneiseProfile;
